import React, { useEffect, useState, useCallback } from 'react';
import { PlusIcon, BookOpenIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { MindBookContent, MindBookFilters, MindBookFormData } from '../types/mindbook';
import { ContentStatus } from '../types';
import { mindBookService } from '../services/mindBookService';
import { MindBookForm } from '../components/forms/MindBookForm';
import { getErrorMessage } from '../utils/errors';
import { PageLayout } from '../components/ui/PageLayout';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Benachrichtigung } from '../components/ui/Benachrichtigung';
import { Rueckfrage } from '../components/ui/Rueckfrage';
import { useBenachrichtigung } from '../hooks/useBenachrichtigung';
import { useRueckfrage } from '../hooks/useRueckfrage';
import { Filterleiste } from '../components/ui/Filterleiste';
import { SeitenblaetterFuss } from '../components/ui/SeitenblaetterFuss';
import { seitenwechsel, statusAuswahl, zugriffAuswahl } from '../components/ui/filterZustand';

// Content → Formular-Daten (Edit). URLs aus contentData.mindbook (kanonisch); Kapitel aus den
// CHAPTER-Tracks (position-geordnet → startMs).
const mapContentToFormData = (mb: MindBookContent): Partial<MindBookFormData> => {
  const d = mb.contentData?.mindbook || {};
  const m = mb.metadata || {};
  const chapters = (mb.tracks || [])
    // ⚠️ Geduldete Handkopie des Prisma-Werts `TrackKind.CHAPTER`, bewusst nicht umgebaut.
    // Dass das Literal existiert, hält `types/__tests__/trackKindVertrag.test.ts` fest — fiele
    // `CHAPTER` aus dem Prisma-Enum, filterte diese Zeile still auf einen Wert, den es nicht gibt.
    .filter((t) => (t.kind || '').toUpperCase() === 'CHAPTER')
    .slice()
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((t, i) => ({ title: (t.title && t.title.trim()) || `Kapitel ${i + 1}`, startMs: typeof t.startMs === 'number' ? t.startMs : 0 }));
  return {
    title: mb.title,
    author: (m as { author?: string }).author || '',
    description: mb.description || '',
    shortDescription: m.shortDescription || '',
    duration: m.duration || '',
    keywords: m.keywords || '',
    topicCategories: Array.isArray(m.topicCategories) ? m.topicCategories : [],
    coverImageUrl: m.coverImage || d.coverImage || '',
    // ⛔ Keine Vorbefuellung der zwei mp3-Adressen mehr: das Formular fuehrt die Felder nicht
    // mehr (Phase 3 „Contract“, 2026-09-01). Der Rueckfall-Zug `d.x || m.x || m.mp3Url || d.mp3Url`
    // stand hier, weil die Adresse an vier Stellen liegen konnte; er beschreibt jetzt nur noch,
    // wo tote Daten wohnen. Was das Werk WIRKLICH an Dateien hat, zeigt die Fassungs-Uebersicht
    // im Formular — die liest den Speicher, nicht `metadata`.
    markerTimecodeCSVUrl: d.markerTimecodeCSVUrl || m.markerTimecodeCSVUrl || '',
    chapters,
    isPremium: mb.isPremium,
    tags: mb.tags || [],
    status: (mb.status as ContentStatus) || ContentStatus.DRAFT,
  };
};

const MindBookManagement: React.FC = () => {
  const [items, setItems] = useState<MindBookContent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<MindBookContent | null>(null);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [filters, setFilters] = useState<MindBookFilters>({ page: 1, limit: 10, search: '', status: undefined, isPremium: undefined });
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });
  // Zustand, Zeitmessung und Aufräumen des Balkens stecken im Haken. Der Fehler bleibt jetzt
  // stehen, statt nach drei Sekunden zu verschwinden — Begründung in `Benachrichtigung.tsx`.
  const { inhalt: benachrichtigung, melde, schließe } = useBenachrichtigung();
  // ⚠️ Das Feld heißt `rückfrage`, nicht `inhalt` — der Name daneben ist schon vergeben. Der Haken
  // ist genau dafür so benannt, siehe `useRueckfrage.ts`.
  const { rückfrage, frage, antworte } = useRueckfrage();

  // Memoisiert, damit der Effekt sie in seine Abhängigkeiten nehmen kann. Die Identität wechselt
  // genau dann, wenn sich `filters` ändert — `melde` ist über `useCallback` stabil.
  const fetchItems = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await mindBookService.getMindBooks(filters);
      setItems(res.data || []);
      setPagination(res.pagination || { page: 1, limit: 10, total: 0, totalPages: 0 });
    } catch (error: unknown) {
      setItems([]);
      melde('error', getErrorMessage(error, 'Fehler beim Laden der MindBooks'));
    } finally {
      setIsLoading(false);
    }
  }, [filters, melde]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const setFilter = <K extends keyof MindBookFilters>(key: K, value: MindBookFilters[K]) =>
    setFilters((p) => ({ ...p, [key]: value, page: 1 }));

  const handleCreate = () => { setEditing(null); setIsFormLoading(false); setShowForm(true); };

  const handleEdit = async (mb: MindBookContent) => {
    setShowForm(true); setIsFormLoading(true); setEditing(null);
    try {
      const full = await mindBookService.getMindBookById(mb.id);
      setEditing(full);
    } catch (error: unknown) {
      melde('error', `Fehler beim Laden: ${getErrorMessage(error)}`);
      setShowForm(false);
    } finally {
      setIsFormLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    // Titel VOR der Frage lesen; gelöscht wird über die übergebene `id`. Damit kann zwischen Frage
    // und Ausführung nichts veralten — `editing` darf sich ändern, das Ziel steht seit dem Klick fest.
    const titel = (editing?.id === id ? editing : items.find((mb) => mb.id === id))?.title;
    // `gefahr`, unstrittig: das Backend fährt `tx.content.delete()` — ein echtes DELETE, kein
    // Statuswechsel (`MindBookService.deleteMindBookContent`). Die Kapitel liegen als Spuren
    // (`kind='CHAPTER'`) am selben Inhalt und hängen per `onDelete: Cascade` daran; an den Spuren
    // wiederum die Playlist-Einträge. Favoriten und gemerkte Einträge cascaden ebenfalls.
    //
    // Die Bibliothek stand hier bewusst NICHT, solange sie gelogen gewesen wäre: `library_pins` und
    // `folder_items` sind polymorph (itemType/itemId) und haben keinen FK auf den Inhalt — die DB
    // räumt sie nicht mit weg. Seit `deleteMindBookContent` `purgeLibraryReferences` in derselben
    // Transaktion ruft, hält der Code die Zusage, also darf sie im Text stehen.
    //
    // Was NICHT verschwindet und deshalb auch nicht versprochen wird: die Revisions-Schnappschüsse
    // (Soft-Referenz, kein FK) sowie im Medienspeicher die Originale UND die Streaming-Fassungen.
    const bestätigt = await frage({
      titel: 'MindBook endgültig löschen?',
      text:
        `${titel ? `„${titel}"` : 'Dieses MindBook'} wird aus der Datenbank entfernt — nicht bloß ` +
        'auf Entwurf gesetzt und nicht archiviert.\n\n' +
        'Mit ihm verschwinden seine Kapitel-Marken und alle Verweise der Nutzerinnen und Nutzer ' +
        'darauf: Favoriten, gemerkte Einträge, Playlist-Einträge sowie Anheftungen und ' +
        'Ordner-Zuordnungen in der Bibliothek. Über diese Oberfläche ist das nicht rückgängig zu ' +
        'machen; die hochgeladenen Dateien und ihre Streaming-Fassungen bleiben im Medienspeicher ' +
        'liegen.',
      gefahr: true,
    });
    if (!bestätigt) return;
    try {
      await mindBookService.deleteMindBook(id);
      melde('success', 'MindBook gelöscht');
      setShowForm(false); setEditing(null); fetchItems();
    } catch {
      melde('error', 'Fehler beim Löschen');
    }
  };

  const handleSubmit = async (data: MindBookFormData) => {
    try {
      if (editing) { await mindBookService.updateMindBook(editing.id, data); melde('success', 'MindBook aktualisiert'); }
      else { await mindBookService.createMindBook(data); melde('success', 'MindBook erstellt'); }
      setShowForm(false); setEditing(null); fetchItems();
    } catch (error: unknown) {
      melde('error', `Fehler beim Speichern: ${getErrorMessage(error)}`);
    }
  };

  const coverOf = (mb: MindBookContent) => mb.metadata?.coverImage || mb.metadata?.thumbnail || mb.contentData?.mindbook?.coverImage || '';

  // Überschrift der Seitenvorlage. Hing vorher an zwei getrennten `return`-Anweisungen; jetzt an
  // einer Stelle, damit es unter dem Balken nur noch EINE Weiche gibt.
  //
  // ⚠️ Der Ladezustand bekommt damit eine Titelzeile, die er vorher nicht hatte — er kam als
  // nackter `<div>` ganz ohne `PageLayout` zurück, begründet mit „welches MindBook geladen wird,
  // steht erst nach dem Laden fest". Das trägt als Sonderfall nicht: „MindBook laden..." nennt
  // genau so wenig einen Titel wie „Audio laden..." und „Video laden..." bei den beiden
  // Geschwisterseiten, und die fahren es seit jeher. Die Alternative wäre gewesen, den
  // Ladezustand als dritten `return` außerhalb der Vorlage stehen zu lassen — dann stünde der
  // Balken in genau diesem Zustand wieder nicht im Baum, also der Fehler, den dieser Umbau
  // schließt. Bewusste Angleichung an die Mehrheit, kein Nebeneffekt.
  const seitenTitel = () => {
    if (!showForm) return 'MindBook-Verwaltung';
    if (isFormLoading) return 'MindBook laden...';
    return editing ? 'MindBook bearbeiten' : 'Neues MindBook';
  };

  const formularZweig = () => {
    if (isFormLoading) {
      return (
        <div className="text-center py-12"><div className="loading-spinner mx-auto w-8 h-8" /><p className="mt-4 text-secondary-600">Lade MindBook...</p></div>
      );
    }
    return (
      <MindBookForm
        key={editing?.id || 'new-mindbook'}
        initialData={editing ? mapContentToFormData(editing) : {}}
        onSubmit={handleSubmit}
        onCancel={() => { setShowForm(false); setEditing(null); }}
        isLoading={isFormLoading}
        mindBookId={editing?.id}
        onDelete={handleDelete}
      />
    );
  };

  const listenZweig = () => (
    <>
      {/* Feld-Definitionen aus `filterFelder.ts`. Diese Seite hat bewusst KEINEN Produkttyp-Filter:
          sie führt genau einen (`mind-book`). */}
      <Filterleiste
        suche={filters.search || ''}
        onSuche={(wert) => setFilter('search', wert)}
        platzhalter="Titel, Beschreibung..."
        auswahlen={[
          statusAuswahl(filters.status, (wert) => setFilter('status', wert)),
          zugriffAuswahl(filters.isPremium, (wert) => setFilter('isPremium', wert)),
        ]}
      />

      {isLoading ? (
        <div className="text-center py-12"><div className="loading-spinner mx-auto w-8 h-8" /><p className="mt-4 text-secondary-600">Lade MindBooks...</p></div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 card">
          <BookOpenIcon className="mx-auto h-12 w-12 text-secondary-400" />
          <p className="mt-4 text-secondary-600">Keine MindBooks gefunden</p>
          <button onClick={handleCreate} className="mt-4 btn btn-primary"><PlusIcon className="w-5 h-5 mr-2" /> Erstes MindBook erstellen</button>
        </div>
      ) : (
        <div className="card overflow-x-auto">
          {/* Kopf- und Zellenmaße kommen aus `.table` (index.css) statt aus handkopierten
              Klassenketten. Was dabei bewusst aufgegeben wurde, steht dort am Regelblock. */}
          <table className="table">
            <thead>
              <tr>
                <th>Cover</th>
                <th>Titel</th>
                <th>Kapitel</th>
                <th>Status</th>
                <th>Zugriff</th>
              </tr>
            </thead>
            <tbody>
              {items.map((mb) => {
                // ⚠️ Zweite geduldete Handkopie von `TrackKind.CHAPTER` — siehe oben in
                // `mapContentToFormData` und `types/__tests__/trackKindVertrag.test.ts`.
                const chapterCount = (mb.tracks || []).filter((t) => (t.kind || '').toUpperCase() === 'CHAPTER').length;
                return (
                  <tr key={mb.id}>
                    <td>
                      <button type="button" onClick={() => handleEdit(mb)} className="cursor-pointer focus:outline-none rounded" title="Bearbeiten">
                        {coverOf(mb) ? (
                          <img src={coverOf(mb)} alt={mb.title} className="h-10 w-10 rounded object-cover" />
                        ) : (
                          <div className="h-10 w-10 rounded bg-secondary-100 flex items-center justify-center"><PhotoIcon className="w-5 h-5 text-secondary-400" /></div>
                        )}
                      </button>
                    </td>
                    <td>
                      <button type="button" onClick={() => handleEdit(mb)} className="text-sm font-medium text-primary-600 hover:text-primary-800 hover:underline text-left" title="Bearbeiten">{mb.title}</button>
                    </td>
                    <td>{chapterCount || '–'}</td>
                    <td><StatusBadge status={mb.status} /></td>
                    <td>
                      {mb.isPremium ? <span className="badge badge-primary">Premium</span> : <span className="badge badge-secondary">Free</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <SeitenblaetterFuss
            seite={pagination.page}
            seitenAnzahl={pagination.totalPages}
            gesamt={pagination.total}
            onSeitenwechsel={seitenwechsel(setFilters)}
          />
        </div>
      )}
    </>
  );

  return (
    <PageLayout
      title={seitenTitel()}
      actions={showForm ? undefined : <button onClick={handleCreate} className="btn btn-primary"><PlusIcon className="w-5 h-5 mr-2" /> Neues MindBook</button>}
    >
      {/* ⚠️ EIN Balken, und zwar VOR der Weiche — das ist der Fix, nicht die Kosmetik daran.
          Vorher endete `if (showForm)` in einem eigenen `return`, und der Balken stand erst
          dahinter, also nur im Listen-Zweig. `handleSubmit` meldet aber im `catch` und lässt das
          Formular bewusst OFFEN, damit die Eingaben nicht verloren gehen: die Fehlermeldung ging
          damit an einen Balken, der in genau diesem Zustand gar nicht im Baum stand. Die Nutzerin
          klickte „Speichern", der Server lehnte ab, und sichtbar passierte nichts. Mit der Weiche
          UNTER dem Balken ist dieser Zustand nicht mehr baubar — derselbe Umbau steht in
          `AudioManagement.tsx`, wo die volle Herleitung nachzulesen ist.

          ⚠️ Unterschied zur Audio-Verwaltung, nachgemessen statt übernommen: `MindBookForm` hält
          KEINE zweite Meldungs-Quelle. Es hat weder `useBenachrichtigung` noch einen eigenen
          `<Benachrichtigung>`; sein `csvError` ist ein Feld-Hinweis
          (`<p className="text-sm text-error-600">`) direkt unter dem Marker-Upload, kein Balken.
          Deshalb bekommt es hier auch keine `notify`-Eigenschaft gereicht — es gäbe nichts
          zusammenzulegen. Ebenso geprüft: `schließe` wird auf dieser Seite an genau einer Stelle
          benutzt, nämlich am Schließen-Knopf des Balkens unten. Es gibt also keinen Aufruf, der
          eine stehende Speicher-Fehlermeldung wegräumen könnte.

          `schwebend` nur im Formular-Zweig — aus den DEKLARIERTEN Werten gerechnet:
          • `MindBookForm` hat eine eigene festgestellte Knopfleiste (`fixed bottom-0 right-0
            left-64 … z-10`) und öffnet den Datei-Wähler (`.bunny-file-picker-overlay`:
            `fixed inset-0 z-50` in `index.css`). Im Textfluss stünde der Balken hinter dem
            Wähler-Schleier und außerhalb des Sichtfelds.
          • `schwebend` bringt `fixed top-4 right-4 z-[60]` mit: 60 schlägt Wähler (50) und
            Knopfleiste (10) erklärt statt über die Dokumentreihenfolge. Der Balken steht VOR
            beiden im Baum, bei Gleichstand verlöre er also. ⚠️ Die Leiste dieses Formulars trägt
            `z-10`, nicht die `z-30` von Audio und Video — der Abstand zu `z-[60]` ist hier größer,
            der Wähler mit `z-50` bleibt aber der maßgebliche Nachbar.
          • OBEN rechts, nicht unten: unten rechts klebt die Knopfleiste des Formulars an derselben
            Kante und trägt dort „Speichern".
          • Der Listen-Zweig hat weder Overlay noch festgestellte Leiste und bleibt deshalb bei der
            Mehrheitsfassung im Textfluss.

          ⚠️ Was diese Rechnung NICHT ist: eine Trefferprobe im laufenden Browser. Unter Vitest ist
          sie gar nicht messbar — jsdom hat kein Layout, `getBoundingClientRect` meldet Nullen. Die
          Tests nageln deshalb die deklarierten Stapel- und Positionswerte fest, nicht Pixel. */}
      <Benachrichtigung inhalt={benachrichtigung} onSchließen={schließe} schwebend={showForm} />

      {/* Die Rückfrage steht ÜBER der Weiche, genau wie der Balken darüber — und der Grund ist
          derselbe.

          ⚠️ Gemessen, nicht von der Nachbarseite übernommen: gefragt wird hier heute
          ausschließlich aus dem FORMULAR-Zweig. `handleDelete` hat genau einen Aufrufer, nämlich
          die `onDelete`-Eigenschaft von `MindBookForm` (Knopf „MindBook löschen" in der
          festgestellten Leiste); die Liste hat keine Löschen-Spalte. Trotzdem gehört der Dialog
          NICHT in `formularZweig()`:
          • `formularZweig()` hat selbst ZWEI Rückgaben — im Ladezustand kommt der Spinner zurück
            und `MindBookForm` gar nicht. Ein Dialog daneben stünde in genau einer davon.
          • Der Erfolgspfad ruft `setShowForm(false)`: der Zweig, in dem gefragt wurde, verschwindet
            als Folge der Antwort.
          • Bekäme die Liste morgen einen Löschen-Knopf, wäre die Frage aus dem Listen-Zweig
            unsichtbar — dieselbe Fehlerklasse, die den Balken hierher gebracht hat.

          Stapelordnung: der Dialog-Schleier liegt mit `z-[55]` über dem Datei-Wähler des Formulars
          (`z-50`) und unter dem schwebenden Balken (`z-[60]`). Im Browser nachgemessen ist das an
          der Themen-Seite, die als einzige alle drei Schichten gleichzeitig zeigen kann. */}
      <Rueckfrage rückfrage={rückfrage} onAntwort={antworte} />

      {showForm ? formularZweig() : listenZweig()}
    </PageLayout>
  );
};

export default MindBookManagement;
